"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { Gift, Loader2, Rocket, AlertCircle } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAgencyStatus } from "@/app/_module/hooks/useAgencyStatus";
import { getReferralOverview } from "@/app/_module/services/referralService";
import { getEnterpriseCoverageStatus, type EnterpriseCoverageStatus } from "@/app/_module/services/api/agency-billing.api";
import { useRewardsStatus, useRewardsOverview, useMyReferrals } from "./_modules/hooks/useRewardsData";
import { getCurrentTier } from "./_modules/types/rewards.types";
import RewardsTierCard from "./_modules/components/RewardsTierCard";
import TierProgressBar from "./_modules/components/TierProgressBar";
import RewardsStats from "./_modules/components/RewardsStats";
import ReferralLinkCard from "./_modules/components/ReferralLinkCard";
import RewardsHistoryTable from "./_modules/components/RewardsHistoryTable";
import HowItWorksSection from "./_modules/components/HowItWorksSection";
import UpcomingRewardsTeaser from "./_modules/components/UpcomingRewardsTeaser";
import { isDemoMode } from "@/app/_module/utils/demoMode";

export default function AgentRewardsPage() {
  const { user, isInitializing } = useUserContext();
  const { branding } = useBranding();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: agencyStatus } = useAgencyStatus();
  const [joining, setJoining] = useState(false);

  // Check enterprise hierarchy via API (recursive CTE — reliable for sub-agency members too)
  const { data: enterpriseStatus } = useQuery<EnterpriseCoverageStatus>({
    queryKey: ['enterprise-coverage-status'],
    queryFn: getEnterpriseCoverageStatus,
    staleTime: 1000 * 60 * 5,
  });

  // Redirect enterprise accounts and enterprise hierarchy members — rewards is for paying agents/agencies only
  useEffect(() => {
    if (agencyStatus?.accountType === 'enterprise' || enterpriseStatus?.isInEnterpriseHierarchy) {
      // Shell-aware: this page is also embedded at /m/rewards
      router.replace(
        typeof window !== 'undefined' && window.location.pathname.startsWith('/m')
          ? '/m/leads'
          : '/leads',
      );
    }
  }, [agencyStatus?.accountType, enterpriseStatus?.isInEnterpriseHierarchy, router]);

  // Check if user has joined rewards (without auto-creating a referral code)
  const { data: rewardsStatus, isLoading: loadingStatus } = useRewardsStatus();
  // DEMO SANDBOX: treat the seat as already enrolled.
  //
  // Otherwise the page opens on a "Join the Rewards Program" CTA and the visitor never
  // sees the feature. Joining for real is not the answer either — it writes an enrolment
  // the seat reset doesn't clear, so the FIRST visitor would join and every visitor after
  // them would inherit it. Skipping straight to the program shows what's actually being
  // demoed and leaves nothing behind.
  const hasJoined = isDemoMode() || rewardsStatus?.hasJoined === true;

  // Only fetch full overview + referrals after confirming user has joined
  const {
    data: overview,
    isLoading: loadingOverview,
    refetch: refetchOverview,
  } = useRewardsOverview(hasJoined);

  const { data: referralsData } = useMyReferrals(
    hasJoined ? { limit: 20 } : undefined,
  );

  const totalConversions = overview?.stats?.totalConversions ?? 0;
  const currentTier = useMemo(() => getCurrentTier(totalConversions), [totalConversions]);

  // "Join" handler — calling getReferralOverview auto-creates the code on backend
  const handleJoin = async () => {
    setJoining(true);
    try {
      await getReferralOverview();
      // Invalidate status so it refetches and shows hasJoined = true
      queryClient.invalidateQueries({ queryKey: ["rewards", "status"] });
      await refetchOverview();
    } catch {
      // error handled by query
    } finally {
      setJoining(false);
    }
  };

  if (isInitializing || loadingStatus) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Show join screen if user hasn't joined yet
  if (!hasJoined) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Card className="border-2 border-dashed border-indigo-200">
          <CardContent className="py-16 text-center">
            <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Gift className="h-10 w-10 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Join the {branding.brandName} Rewards Program
            </h2>
            <p className="text-gray-600 max-w-md mx-auto mb-2">
              Earn free months by referring other agents! For every agent you refer
              who subscribes, you <strong>both</strong> get 1 free month.
            </p>
            <p className="text-sm text-gray-500 max-w-md mx-auto mb-8">
              Climb the ranks from Bronze to Elite as you grow your referral network.
            </p>

            <Button
              size="lg"
              onClick={handleJoin}
              disabled={joining}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {joining ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Rocket className="h-4 w-4 mr-2" />
              )}
              Join Rewards Program
            </Button>

            <div className="mt-10">
              <HowItWorksSection />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading overview after join confirmed
  if (loadingOverview) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">Failed to load rewards data. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Rewards Program</h1>
        <p className="text-sm text-gray-500">
          Refer agents and earn free months for you and your referrals.
        </p>
      </div>

      {/* Tier + Stats */}
      <RewardsTierCard totalConversions={totalConversions} />
      <RewardsStats stats={overview.stats} rewards={overview.rewards} />

      {/* Tier Progress */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Tier Progress</h3>
          <TierProgressBar totalConversions={totalConversions} />
        </CardContent>
      </Card>

      {/* Referral Link */}
      <ReferralLinkCard
        referralUrl={overview.referralUrl}
        code={overview.code}
        customSlug={overview.customSlug}
      />

      {/* History */}
      <RewardsHistoryTable
        referrals={referralsData?.referrals ?? []}
        total={referralsData?.total ?? 0}
      />

      {/* Upcoming rewards teaser */}
      <UpcomingRewardsTeaser />

      {/* How it works */}
      <HowItWorksSection />
    </div>
  );
}
